import { useEffect, useRef, useState, type FC } from 'react'

import { Input, LoaderCircle, Table } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import {
  getQueryFilter,
  getSortedList,
  getWhiteLabelForDealerStyle,
  MAX_DECIMAL_12_4,
  MIN_PRICE,
  useDebounce,
} from '@skytech/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { FormikErrors, FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/App.context'
import { ExportToExcel } from '@/components/ExportToExcel'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import NoResults from '@/components/util/NoResults'
import useRenderLimit from '@/hooks/render-limit-hook'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useProfile } from '@/hooks/user'
import { queryClient } from '@/queryClient'
import { appRoutes } from '@/util/misc'

import { OrderListItem } from '../components/OrderListItem'
import { StatusSelect } from '../components/StatusSelect'
import { StatusTabs } from '../components/StatusTabs'
import type { FormFields } from '../helpers'
import { filterUpdatedData, mapUpdatedOrders, normalizeOrders } from '../helpers'
import { tradeInQueries } from '../queries'
import type { TradeInOrders } from '../resources'
import { tradeInResources } from '../resources'
import type { StatusOriginal, UpdateOrdersType } from '../resources/getTradeInOrders'

import { OrdersTHead } from './OrdersTHead'

export const Orders: FC<{ customerId: number }> = ({ customerId }) => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const currency = useProfile()?.currency

  const { data, isLoading } = useQuery(tradeInQueries.tradeInOrders(), () =>
    tradeInResources.getTradeInOrders(customerId)
  )

  const { locale, whiteLabel } = useAppContext()

  const [querySearch, setSearchQuery] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')

  const [queryStatus] = useSearchParamsByKey('status', 'PRICE_REQUEST')

  const stringMatcher = (item: TradeInOrders) => [
    item.productName,
    item.imeiSerialNumber,
    translate(`trade_in_order_status/${item.orderStatus}`),
    item.estimatedPrice,
    item.finalGrade,
    item.finalPrice,
    item.grade,
    item.assetDate,
    item.customerName,
  ]

  const isQueryStatus = (orderStatus: StatusOriginal) => {
    if (queryStatus === 'INCOMING') {
      return orderStatus === 'PRICE_ESTIMATION' || orderStatus === 'ACCEPTANCE'
    }
    return orderStatus === queryStatus
  }

  const itemsFiltered =
    data?.orders?.filter(item => {
      const isStatusFilterMet = queryStatus
        ? queryStatus === 'ALL'
          ? true
          : isQueryStatus(item.orderStatus)
        : true
      return isStatusFilterMet && getQueryFilter(searchFilter, stringMatcher)(item)
    }) || []

  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof TradeInOrders>()

  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  const updateTradeInOrdersMutation = useMutation(tradeInResources.updateTradeInOrders, {
    onSuccess() {
      queryClient.invalidateQueries(tradeInQueries.tradeInOrders())
    },
  })
  const handleOnFinish = (orderId: string) => {
    const payload: UpdateOrdersType = {
      orders: [
        { orderId, grade: '', price: 0, currencyCode: '', newStatusCode: 'FINISHED', comment: '' },
      ],
    }
    updateTradeInOrdersMutation.mutate({ payload })
  }
  const isPriceRequestOrSentStatus = queryStatus === 'PRICE_REQUEST' || queryStatus === 'SENT'

  const formRef = useRef<FormikProps<FormFields>>(null)
  const mapVisibleList = visibleList
  const normalizedOrders = isPriceRequestOrSentStatus
    ? normalizeOrders(mapVisibleList, queryStatus)
    : visibleList
  const initialValues = { orders: normalizedOrders }

  const onOrdersUpdate = (updatedOrders: TradeInOrders[]) => {
    const updatedData = mapUpdatedOrders(updatedOrders, currency)
    const payload: UpdateOrdersType = { orders: updatedData }
    updateTradeInOrdersMutation.mutate({ payload })
  }

  return (
    <Container onSwipedLeft={() => navigate(appRoutes.home)}>
      <Container.Header>
        <Translation id="Trade In Orders" />
      </Container.Header>

      {isLoading ? (
        // todo: Remove important when LoaderCircle is refactored
        <LoaderCircle
          className="!relative flex w-full justify-center"
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        />
      ) : (
        <>
          {data && <StatusTabs orderCounter={data.orderCounter} />}
          {data && <StatusSelect orderCounter={data.orderCounter} />}
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            enableReinitialize
            validateOnChange
            validateOnBlur
            onSubmit={values => {
              const updatedOrders = filterUpdatedData(values, queryStatus, normalizedOrders)
              if (updatedOrders.length === 0) return

              const errors: FormikErrors<FormFields> = { orders: [] }
              updatedOrders.forEach((order, index) => {
                const orderErrors: any = {}
                const initial = initialValues.orders[index]

                const isPriceReqStatus = queryStatus === 'PRICE_REQUEST'
                const isSentStatus = queryStatus === 'SENT'

                if (isPriceReqStatus) {
                  const isEstimatedPriceDirty = order.estimatedPrice !== initial.estimatedPrice
                  const isGradeDirty = order.grade !== initial.grade

                  if (isEstimatedPriceDirty) {
                    if (!order.estimatedPrice) {
                      orderErrors.estimatedPrice = translate('This field is mandatory')
                    }
                    if (order.estimatedPrice && Number(order.estimatedPrice) > MAX_DECIMAL_12_4) {
                      orderErrors.estimatedPrice = translate('The price is too big')
                    }
                    if (order.estimatedPrice && Number(order.estimatedPrice) < MIN_PRICE) {
                      orderErrors.estimatedPrice = translate('The price is too small')
                    }
                  }
                  if (isGradeDirty && order.grade === null) {
                    orderErrors.grade = translate('This field is mandatory')
                  }
                }

                if (isSentStatus) {
                  const isFinalPriceDirty = order.finalPrice !== initial.finalPrice
                  const isFinalGradeDirty = order.finalGrade !== initial.finalGrade

                  if (isFinalPriceDirty) {
                    if (!order.finalPrice) {
                      orderErrors.finalPrice = translate('This field is mandatory')
                    }
                    if (order.finalPrice && Number(order.finalPrice) > MAX_DECIMAL_12_4) {
                      orderErrors.finalPrice = translate('The price is too big')
                    }
                    if (order.finalPrice && Number(order.finalPrice) < MIN_PRICE) {
                      orderErrors.finalPrice = translate('The price is too small')
                    }
                  }
                  if (isFinalGradeDirty && order.finalGrade === null) {
                    orderErrors.finalGrade = translate('This field is mandatory')
                  }
                }

                if (Array.isArray(errors.orders)) {
                  errors.orders[index] = orderErrors
                }
              })

              if (
                Array.isArray(errors.orders) &&
                errors?.orders?.some((e: any) => Object.keys(e).length > 0)
              ) {
                formRef.current?.setErrors(errors)
                return
              }
              onOrdersUpdate(updatedOrders)
            }}
          >
            {({ values, isValid }) => {
              const isOrdersChanged = JSON.stringify(values) !== JSON.stringify(initialValues)
              return (
                <Form>
                  <div className="flex flex-row items-center justify-between gap-4">
                    <div className="flex gap-4">
                      <div className="items-center justify-between md:flex">
                        <div className="table-actions">
                          <div className="search">
                            <Input
                              id="trade-in-orders-search"
                              type="text"
                              value={searchFilter}
                              placeholder={translate('Search')}
                              onChange={e => setSearchFilter(e.target.value)}
                              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                            />
                          </div>
                        </div>
                      </div>
                      <ExportToExcel
                        data={itemsFiltered}
                        filename={translate('Trade-in orders')}
                        options={{
                          columnFormats: {
                            purchaseDate: 'date',
                            finalPrice: 'amount',
                            estimatedPrice: 'amount',
                          },
                        }}
                      />
                    </div>
                    {isPriceRequestOrSentStatus && (
                      <Button
                        disabled={!isOrdersChanged || !isValid}
                        type="submit"
                        variant="primary"
                        className="m-0"
                        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                      >
                        <Translation id="Save" />
                      </Button>
                    )}
                  </div>
                  {itemsFiltered.length > 0 ? (
                    <div>
                      <Table rowHeight="tall">
                        <OrdersTHead handleSort={handleSort} />
                        <tbody>
                          {isPriceRequestOrSentStatus
                            ? values.orders.map((item, idx) => (
                                <OrderListItem
                                  key={item.orderId}
                                  searchFilter={searchFilter}
                                  index={idx}
                                  handleOnFinish={handleOnFinish}
                                  {...item}
                                />
                              ))
                            : visibleList.map((item, idx) => (
                                <OrderListItem
                                  key={item.orderId}
                                  searchFilter={searchFilter}
                                  index={idx}
                                  handleOnFinish={handleOnFinish}
                                  {...item}
                                />
                              ))}
                        </tbody>
                      </Table>
                      <ListLoaderComponent />
                    </div>
                  ) : (
                    <NoResults />
                  )}
                </Form>
              )
            }}
          </Formik>
        </>
      )}
    </Container>
  )
}
